// Vendor imports
const classNames = require('classnames')
const _ = require('lodash')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
const { TYPES: modelTypes } = require('shared/model-types')
// Client V2 common imports
const AppFooter = require('client-v2/common/containers/app-footer/component-smart')
const { useIsLightweightCollectionSelectModalVisible } = require('client-v2/common/containers/collection-select-modal/hooks')
const { useIsSearchFormVisible } = require('client-v2/common/containers/search-form/hooks')
const Icon = require('client-v2/common/containers/icons/component')
const { useModalDepth } = require('client-v2/common/contexts/modal-depth')
const useClientEffect = require('client-v2/common/hooks/client-effect')
const useInfiniteScroll = require('client-v2/common/hooks/infinite-scroll')
const tag = require('client-v2/common/utils/hyperscript')
// Sibling imports
const SeriesFeedArticle = require('./article')

const bem = BemEntity('SeriesFeed')

const DEFAULT_LOAD_MORE_THRESHOLD = 2000 // px

function SeriesFeed({
  // Values
  className,
  headerElement,
  isMoreAvailable,
  isMoreLoading,
  items,
  // Callbacks
  onArticleBookmark,
  onArticleContentNavigate,
  onArticleDomainNavigate,
  onArticleEmbedDisclose,
  onArticleMoreActions,
  onArticleRate,
  onArticleSeen,
  onArticleShare,
  onLoadMore
}) {
  const scrollableRef = React.useRef()
  const articleHeight = useArticleHeight(scrollableRef)
  const onArticleElementFocus = useArticleElementFocus(scrollableRef)
  const loadMoreThreshold = React.useMemo(
    () => (
      articleHeight === '100%'
      ? DEFAULT_LOAD_MORE_THRESHOLD
      : parseFloat(articleHeight) * 2
    ),
    [ articleHeight ]
  )
  useInfiniteScroll({
    disabled: !isMoreAvailable,
    scrollableRef,
    loadMoreThreshold,
    onLoadMore
  })
  return (
    tag('section', {
      className: classNames(
        className,
        bem.block()
      ),
      role: 'main'
    },
      headerElement && React.cloneElement(headerElement, {
        className: classNames(
          bem.element('header'),
          headerElement.props.className
        )
      }),
      tag('div', {
        ref: scrollableRef,
        className: bem.element('scrollable')
      },
        items.map(({ type, id, article, meta, pageContext }) => {
          if (type !== modelTypes.ARTICLE) {
            return null
          }
          return (
            tag(SeriesFeedArticle, {
              key: `${type}:${id}`,
              // Values
              className: bem.element('item'),
              height: articleHeight,
              article,
              pageContext,
              rating: meta.rating,
              recPosition: meta.recPosition,
              recReason: meta.recReason,
              // Callbacks
              onArticleBookmark,
              onArticleContentNavigate,
              onArticleDomainNavigate,
              onArticleElementFocus,
              onArticleEmbedDisclose,
              onArticleMoreActions,
              onArticleRate,
              onArticleSeen,
              onArticleShare
            })
          )
        }),
        isMoreLoading && (
          tag(Icon, {
            className: bem.element('loading'),
            id: 'spinner'
          })
        ),
        (!isMoreAvailable && !isMoreLoading) && (
          tag(AppFooter, {
            className: bem.element('footer')
          })
        )
      )
    )
  )
}

const NEXT_ARTICLE_PEEK = 48 // px
/** @type {readonly ScrollIntoViewOptions} */
const FOCUS_SCROLL_INTO_VIEW_OPTIONS = { behavior: 'smooth', block: 'start' }

function useArticleHeight(scrollableRef) {
  const [ articleHeight, setArticleHeight ] = React.useState('100%')
  useClientEffect(
    () => {
      const scrollableEl = scrollableRef.current
      function adjustArticleHeight() {
        if (!scrollableEl) {
          setArticleHeight('100%')
        } else {
          setArticleHeight(`${scrollableEl.clientHeight - NEXT_ARTICLE_PEEK}px`)
        }
      }
      adjustArticleHeight()
      const adjustArticleHeightThrottled = _.throttle(adjustArticleHeight, 80) // 5 frames
      window.addEventListener('resize', adjustArticleHeightThrottled)
      return () => {
        window.removeEventListener('resize', adjustArticleHeightThrottled)
        adjustArticleHeightThrottled.cancel()
      }
    },
    [ scrollableRef ]
  )
  return articleHeight
}

/**
 * @param {React.RefObject<HTMLElement>} scrollableRef
 * @returns {(el: HTMLElement) => void}
 */
function useArticleElementFocus(scrollableRef) {
  const [ focusEl, setFocusEl ] = React.useState()
  // Refocus on the article when the window resizes
  useClientEffect(
    () => {
      if (!focusEl) {
        return
      }
      let cancelScrollToFocus = () => {}
      function scrollToFocus() {
        cancelScrollToFocus()
        if (focusEl) {
          cancelScrollToFocus = aggressivelyScrollIntoView(
            focusEl,
            FOCUS_SCROLL_INTO_VIEW_OPTIONS
          )
        }
      }
      const scrollToFocusDebounced = _.debounce(scrollToFocus, 256, { leading: true })
      window.addEventListener('resize', scrollToFocusDebounced)
      return () => {
        window.removeEventListener('resize', scrollToFocusDebounced)
        scrollToFocusDebounced.cancel()
        cancelScrollToFocus()
      }
    },
    [ focusEl ]
  )
  // Support arrow keys to focus on next/previous article
  const isModalVisible = _.some([
    useModalDepth() > 0,
    useIsLightweightCollectionSelectModalVisible(),
    useIsSearchFormVisible()
  ])
  useClientEffect(
    () => {
      const scrollableEl = scrollableRef.current
      if (!scrollableEl || isModalVisible) {
        return
      }
      function onKeyDown(event) {
        let newFocusEl
        if (event.key === 'ArrowUp') {
          newFocusEl = focusEl && focusEl.previousSibling || scrollableEl.firstChild
        } else if (event.key === 'ArrowDown') {
          newFocusEl = focusEl && focusEl.nextSibling || scrollableEl.lastChild
        }
        if (newFocusEl) {
          newFocusEl.scrollIntoView(FOCUS_SCROLL_INTO_VIEW_OPTIONS)
        }
      }
      window.addEventListener('keydown', onKeyDown)
      return () => {
        window.removeEventListener('keydown', onKeyDown)
      }
    },
    [ scrollableRef, focusEl, isModalVisible ]
  )
  return setFocusEl
}

module.exports = SeriesFeed

/**
 * @param {HTMLElement} el
 * @param {boolean | ScrollIntoViewOptions} [options]
 * @returns {() => void} cancellation function
 */
function aggressivelyScrollIntoView(el, options) {
  let cancelId
  let prevTop
  let prevHeight
  function keepTrying() {
    const { top, height } = el.getBoundingClientRect()
    if (prevTop !== top || prevHeight !== height) {
      el.scrollIntoView(options)
      prevTop = top
      prevHeight = height
      cancelId = setTimeout(keepTrying, 256) // 16 frames
    }
  }
  cancelId = setTimeout(keepTrying, 16) // 1 frame
  return () => {
    if (cancelId) {
      clearTimeout(cancelId)
      cancelId = undefined
    }
  }
}
